/**
 * Dịch nhãn và giá trị do MÁY CHỦ trả về sang ngôn ngữ đang bật.
 *
 * Vì sao cần: nhiều màn (chi tiết giao dịch, chi tiết tiền gửi, tất toán, thẻ…)
 * không tự dựng nhãn mà render thẳng mảng key/value backend trả về —
 * `detailObjectList`, `accountInfoFields`, `interestRateFields`, `transactionInfo`.
 * Backend hiện chỉ trả tiếng Việt, nên ở giao diện tiếng Anh cả cột nhãn lẫn một
 * phần giá trị vẫn là tiếng Việt.
 *
 * Cách làm: những nhãn/giá trị này là TẬP HỮU HẠN và cố định theo nghiệp vụ, nên
 * frontend tra bảng chuỗi tiếng Việt -> khoá i18n. Không khớp thì TRẢ NGUYÊN VĂN —
 * dữ liệu thật của khách (tên công ty, tên người, số tài khoản) không bao giờ bị
 * đụng tới, và backend đổi câu chữ cũng chỉ mất bản dịch chứ không mất nội dung.
 *
 * Đây là giải pháp tạm ở frontend. Cách đúng về lâu dài là backend trả thêm
 * `nameEn`/`valueEn` (hoặc mã ổn định) — khi có thì gỡ bảng này đi.
 */
import i18n from "i18n/i18n";

/** Chuỗi tiếng Việt (đã chuẩn hoá) -> khoá i18n. */
const DICTIONARY: Record<string, string> = {
  // ── nhãn: thông tin khoản tiền gửi ────────────────────────────────────
  "số tiền gửi": "DEPOSIT_AMOUNT",
  "tài khoản nguồn": "SOURCE_ACCOUNT",
  "loại tiền gửi": "DEPOSIT_TYPE",
  "loại kỳ hạn": "TERM_TYPE",
  "hình thức lĩnh lãi": "INTEREST_COLLECTION_METHOD",
  "hình thức tính lãi": "INTEREST_CALCULATION_METHOD",
  "lãi đầu kì dự kiến": "EXPECTED_START_OF_TERM_INTEREST",
  "lãi đầu kỳ dự kiến": "EXPECTED_START_OF_TERM_INTEREST",
  "lãi cuối kỳ dự kiến": "EXPECTED_END-OF-PERIOD_PROFIT",
  "hình thức tái tục": "ROLLOVER_METHOD",
  "hình thức tái tục gốc": "PRINCIPAL_RENEWAL_FORM",
  "hình thức tái tục gốc, lãi": "PRINCIPAL_AND_INTEREST_RENEWAL_FORM",
  "hình thức gửi": "DEPOSIT_METHOD",
  "loại tiết kiệm": "SAVINGS_TYPE",
  "tên sản phẩm": "PRODUCT_NAME",

  // ── nhãn: tài khoản ───────────────────────────────────────────────────
  "số tài khoản": "ACCOUNT_NUMBER",
  "tên tài khoản": "ACCOUNT_NICKNAME",
  "số tài khoản tiết kiệm": "SAVINGS_ACCOUNT_NUMBER",
  "tài khoản thụ hưởng": "BENEFICIARY_ACCOUNT",
  "đơn vị tiền tệ": "CURRENCY",
  stk: "ACCOUNT_NO_ABBR",

  // ── nhãn: giao dịch ───────────────────────────────────────────────────
  "mã tham chiếu": "REFERENCE_ID",
  "mã giao dịch": "TRANSACTION_CODE",
  "loại giao dịch": "TRANSFER_TYPE",
  "trạng thái duyệt": "APPROVE_STATUS",
  "trạng thái": "STATUS",
  "người tạo": "INITIATOR",
  "người duyệt": "REVIEWER",
  "ngày tạo": "DATE_CREATED",
  "ngày duyệt": "APPROVAL_DATE",
  "lý do": "REASON",
  "nội dung": "CONTENT",
  "người thụ hưởng": "BENEFICIARY",
  "ngày giao dịch": "TRANSACTION_DATE",

  // ── nhãn: ngày tháng, lãi, phí ────────────────────────────────────────
  "ngày mở": "OPEN_DATE",
  "ngày hiệu lực": "EFFECTIVE_DATE",
  "ngày đáo hạn": "MATURITY_DATE",
  "ngày bắt đầu": "START_DATE",
  "thời gian đáo hạn": "MATURITY_TIME",
  "ngày tạo yêu cầu tất toán": "SETTLEMENT_REQUEST_DATE",
  "số ngày hưởng lãi": "NUMBER_OF_DAYS_TO_EARN_INTEREST",
  "lãi suất": "INTEREST_RATE",
  "lãi suất đúng hạn": "ON_TIME_INTEREST_RATE",
  "lãi suất rút trước hạn": "EARLY_WITHDRAWAL_INTEREST_RATE",
  "lãi suất(tất toán trước hạn)": "INTEREST_RATE_EARLY_SETTLEMENT",
  "lãi suất (tất toán trước hạn)": "INTEREST_RATE_EARLY_SETTLEMENT",
  "tổng tiền lãi(tất toán trước hạn)": "TOTAL_INTEREST_EARLY_SETTLEMENT",
  "tổng tiền lãi (tất toán trước hạn)": "TOTAL_INTEREST_EARLY_SETTLEMENT",
  "lãi đã hưởng(do rút trước hạn)": "INTEREST_EARNED_EARLY_WITHDRAWAL",
  "lãi đã hưởng (do rút trước hạn)": "INTEREST_EARNED_EARLY_WITHDRAWAL",
  "lãi tạm tính cho gốc còn lại": "ESTIMATED_INTEREST_REMAINING_PRINCIPAL",
  "tiền lãi": "INTEREST_MONEY",
  "số tiền gốc": "PRINCIPAL_SUM",
  "số tiền lãi": "INTEREST_SUM",
  "tổng số tiền nhận": "TOTAL_DISBURSEMENT_AMOUNT",
  phí: "FEE",
  "lịch sử tái tục": "RENEWAL_HISTORY",

  // ── nhãn: thẻ ─────────────────────────────────────────────────────────
  "số thẻ": "CARD_NUMBER",
  "chủ thẻ": "CARDHOLDER",
  "loại thẻ": "CARD_TYPE",

  // ── giá trị: giới tính (OBD eKYC — BE trả VN ở gender/sexDisplay) #55908 ─
  "nam": "MALE",
  "nữ": "FEMALE",
  "nam giới": "MALE",
  "nữ giới": "FEMALE",
  "khác": "GENDER_OTHER",

  // ── giá trị: sản phẩm tiền gửi ────────────────────────────────────────
  "tiền gửi có kỳ hạn thường": "STANDARD_TERM_DEPOSIT",
  "tiền gửi rút linh hoạt": "FLEXIBLE_WITHDRAWAL_DEPOSIT",
  "tiền gửi rút gốc linh hoạt": "FLEXIBLE_PRINCIPAL_WITHDRAWAL_DEPOSIT",
  "tiền gửi tích luỹ": "ACCUMULATION_DEPOSIT",
  "tiền gửi tích lũy": "ACCUMULATION_DEPOSIT",

  // ── nhãn: tiền gửi tích luỹ / định kỳ ─────────────────────────────────
  "chu kỳ gửi": "DEPOSIT_CYCLE",
  "tiền gửi định kỳ": "RECURRING_DEPOSIT",
  "hình thức tích lũy": "ACCUMULATION_METHOD",
  "hình thức tích luỹ": "ACCUMULATION_METHOD",
  "lãi suất kỳ hạn gốc": "BASE_TERM_INTEREST_RATE",
  // BE dùng cả hai thứ tự từ cho cùng một nhãn — giữ cả hai, thiếu một là trượt.
  "lãi dự kiến cuối kỳ": "EXPECTED_END-OF-PERIOD_PROFIT",

  // ── giá trị: mô tả ngắn 3 sản phẩm tiền gửi ───────────────────────────
  "là sản phẩm tiền gửi có kỳ hạn truyền thống dành cho khách hàng tổ chức với kỳ hạn gửi và hình thức lĩnh lãi đa dạng":
    "STANDARD_TERM_DEPOSIT_DESC",
  "là sản phẩm tiền gửi cho phép khách hàng rút một phần tiền gốc trong hợp đồng tiền gửi ban đầu mà không ảnh hưởng tới kỳ hạn và số tiền còn lại chưa rút.":
    "FLEXIBLE_WITHDRAWAL_DEPOSIT_DESC",
  "là sản phẩm tiền gửi cho phép khách hàng tích lũy tiền gửi định kỳ hoặc linh hoạt trong suốt thời gian gửi nhằm gia tăng số dư tích lũy và đạt được các mục tiêu tài chính trong tương lai.":
    "ACCUMULATION_DEPOSIT_DESC",

  // ── giá trị: hình thức lĩnh lãi / tái tục / gửi ───────────────────────
  "đầu kỳ": "START_OF_TERM",
  "cuối kỳ": "END_OF_TERM",
  "định kỳ": "PERIODIC",
  "không quay vòng": "NO_ROLLOVER",
  "tái tục gốc": "PRINCIPAL_ROLLOVER",
  "quay vòng gốc": "PRINCIPAL_ROLLOVER",
  "lãi nhập gốc quay vòng": "INTEREST_ROLLED_OVER_TO_PRINCIPAL",
  "thủ công": "MANUAL",
  "tự động": "AUTOMATIC",
  "hàng tháng": "MONTHLY",
  "hàng quý": "QUARTERLY",

  // ── giá trị: loại giao dịch ───────────────────────────────────────────
  "tất toán tài khoản": "CLOSE_ACCOUNT_TRANSACTION",
  "tất toán tiền gửi": "FINALIZE_DEPOSIT",
  "rút tiền một phần": "PARTIAL_WITHDRAWAL_LABEL",
  // #56342: cột "Loại giao dịch" màn kết quả rút gốc từng phần
  "rút gốc từng phần": "REMOVE_THE_ROOT_PART_BY_PART",
  "chuyển tiền liên ngân hàng": "INTERBANK_TRANSFER_LABEL",
  "chuyển tiền quốc tế": "INTERNATIONAL_TRANSFER",
  "chuyển tiền theo lô": "BULK_TRANSFER",
  "thanh toán hóa đơn": "BILL_PAYMENT",
  "thanh toán hoá đơn": "BILL_PAYMENT",
  "tiết kiệm": "SAVING",
  "mở tài khoản tiền gửi": "OPEN_DEPOSIT_ACCOUNT",
  // #56084: cột "Loại giao dịch" (DS quản lý GD) còn VN cho loại tiền vay & bảo lãnh.
  "bảo lãnh dự thầu": "BID_BOND_GUARANTEE",
  "tất toán khoản vay": "LOAN_CLOSURE",
  "thanh toán khoản vay": "LOAN_PAYMENT",
  "trả trước hạn một phần": "PARTIAL_EARLY_REPAYMENT",
  "chuyển tiền trong nước": "DOMESTIC_TRANSFER",

  // ── giá trị: loại dịch vụ hoá đơn ─────────────────────────────────────
  điện: "SERVICE_ELECTRICITY",
  nước: "SERVICE_WATER",
  internet: "SERVICE_INTERNET",
  "điện thoại": "SERVICE_TELEPHONE",

  // ── giá trị: kết quả thao tác ─────────────────────────────────────────
  "thành công": "SUCCESS",

  // ── giá trị: trạng thái ───────────────────────────────────────────────
  "chờ duyệt": "WAITING_APPROVE",
  "đã trả lại": "RETURNED_STATUS",
  "đã duyệt": "APPROVED",
  "đang chờ": "PENDING_STATUS",
  // #56083: trạng thái thẻ (statusLabel BE trả VN) — dự phòng khi mã trạng thái
  // không phải A/L. Mã L/A đã map trực tiếp ở CardDetailWrapper nên KHÔNG cần
  // các biến thể "khóa" ở đây; chỉ giữ "hoạt động" làm fallback chung.
  "hoạt động": "ACTIVE",

  // ── disbursement (đề nghị giải ngân): giá trị cấu hình BE trả VN ───────
  // Options loại hạn mức / mục đích / kỳ hạn / ngày trả lãi / phương thức trả
  // gốc lấy từ LOAN_CONFIG_PARAM (cfg) chỉ có `name` tiếng Việt; các màn review
  // (MakerWizard) và chi tiết (DisbursementDetail/ApproveScreen) render thẳng
  // chuỗi này nên EN vẫn ra tiếng Việt. Kỳ hạn "9 tháng" đã có UNITS lo.
  // Loại hạn mức
  "vốn lưu động": "WORKING_CAPITAL",
  "trung dài hạn": "MEDIUM_AND_LONG_TERM",
  "thấu chi": "OVERDRAFT",
  "bảo lãnh": "GUARANTEE",
  // Phương thức trả gốc
  "gốc trả cuối kỳ — lãi hàng tháng": "PRINCIPAL_AT_MATURITY_MONTHLY_INTEREST",
  "trả góp đều hàng tháng (gốc + lãi)": "EQUAL_MONTHLY_INSTALMENTS",
  // Ngày trả lãi định kỳ
  "ngày 5": "DAY_5",
  "ngày 10": "DAY_10",
  "ngày 25": "DAY_25",
  // Mục đích sử dụng vốn
  "mua nguyên vật liệu": "PURCHASE_OF_RAW_MATERIALS",
  "mua hàng hóa": "PURCHASE_OF_GOODS",
  "trả lương nhân viên": "PAY_EMPLOYEE_SALARIES",
  "chi phí vận hành": "OPERATING_EXPENSES",
  "thanh toán nhà cung cấp": "PAY_SUPPLIERS",
  "mua tài sản cố định": "PURCHASE_OF_FIXED_ASSETS",
  "xây dựng nhà xưởng": "FACTORY_CONSTRUCTION",
  "mua máy móc thiết bị": "PURCHASE_OF_MACHINERY_EQUIPMENT",
  "mở rộng sản xuất": "PRODUCTION_EXPANSION",
  "bù đắp thiếu hụt vốn tạm thời": "COVER_TEMPORARY_CAPITAL_SHORTAGE",
  "chi trả phát sinh": "PAYMENT_OF_INCIDENTAL_EXPENSES",
  // Trạng thái đề nghị giải ngân (statusLabel BE trả VN + const STATUS ở detailData)
  "hoàn lại": "RETURNED",
  "đang xử lý tại ngân hàng": "BANK_PROCESSING",
  "nháp": "DRAFT",
  "hoàn thành": "TRANSFER_STEP_FOUR_LABEL",
  "từ chối": "REJECT",
  "đã hủy": "CANCELLED",
  // Tên loại chứng từ giải ngân ĐÃ GỠ (#55911): backend nay trả sẵn theo ngôn ngữ request,
  // tra bảng dịch I18N_TEXT theo ID bản ghi gốc. Bảng map ở đây chỉ phủ 10 trên 79 tên, nên
  // thêm một tên hồ sơ vào DB là màn hình im lặng hiện tiếng Việt.
  //
  // Nó còn sửa cả chữ TIẾNG VIỆT: "HĐ mua bán hàng hóa / HĐ kinh tế" bị hiện thành
  // "HĐ mua bán / HĐ kinh tế", "Phương án sử dụng vốn vay" thành "Phương án sử dụng vốn" —
  // tức người dùng tiếng Việt cũng thấy tên khác với cái Nghiệp vụ cấu hình.

  // ── manage-transaction (namnh): cột "Loại giao dịch" + phụ đề khoản vay ─
  // BE trả businessTypeName/businessSubTypeName tiếng Việt ở màn Transaction
  // list (#55971/#56084). Map sang key có sẵn để EN mode ra tiếng Anh.
  // Loại giao dịch thẻ / tài khoản ("tất toán tài khoản" đã có ở trên)
  "khóa thẻ ghi nợ": "LOCK_DEBIT_CARD",
  "thay đổi hạn mức thẻ ghi nợ": "CHANGE_DEBIT_CARD_LIMIT",
  // #56060/#56063: màn xác nhận GD thẻ (CardActionConfirmView) — value "Loại
  // giao dịch" + 2 nhãn hạn mức BE trả tiếng Việt, dịch để EN mode ra tiếng Anh.
  "thiết lập hạn mức giao dịch thẻ": "CARD_TXN_LIMIT_SETUP",
  "hạn mức trong ngày": "DAILY_LIMIT",
  "hạn mức một giao dịch": "PER_TRANSACTION_LIMIT",
  // #56063 (reopen): màn CHI TIẾT/kết quả GD thẻ (CardActionResultView) — value
  // "Loại giao dịch" ("Cập nhật hạn mức thẻ …") + 2 nhãn hạn mức MỚI; BE trả
  // tiếng Việt nên beText passthrough. Map cả biến thể ghi nợ / tín dụng.
  "cập nhật hạn mức thẻ ghi nợ": "UPDATE_DEBIT_CARD_LIMIT",
  "cập nhật hạn mức thẻ tín dụng": "UPDATE_CREDIT_CARD_LIMIT",
  "hạn mức trong ngày mới": "NEW_DAILY_LIMIT",
  "hạn mức một giao dịch mới": "NEW_PER_TRANSACTION_LIMIT",
  "đóng tài khoản": "CLOSE_ACCOUNT",
  // Biến thể có LỖI CHÍNH TẢ trong data BE (đã báo BE sửa nguồn VN); map để
  // EN mode vẫn ra đúng và không lộ typo: "Nơ" (thiếu dấu nặng), "khoảng".
  "khóa thẻ ghi nơ": "LOCK_DEBIT_CARD",
  "tất toán tài khoảng": "CLOSE_ACCOUNT_TRANSACTION",
  // Phụ đề (businessSubTypeName) loại thanh toán khoản vay
  // ("trả trước hạn một phần" đã có ở trên)
  "trả kỳ hiện tại": "PAY_CURRENT_PERIOD",
  "trả nợ quá hạn": "OVERDUE_REPAYMENT",

  // ── chức danh người dùng (BIZ_ROLES.BIZ_ROLE_NAME) ──────────────────────
  // #55932: "Prepared by: PHÙNG THỊ THÙY (Giám đốc)" — TÊN là dữ liệu thật nên giữ nguyên,
  // nhưng CHỨC DANH trong ngoặc là nhãn nghiệp vụ, tập hữu hạn, phải đổi theo ngôn ngữ.
  // customer-service chỉ trả BIZ_ROLE_NAME (tiếng Việt): ApprovalLevelRepository select
  // br.BIZ_ROLE_NAME mà KHÔNG select bu.BIZ_ROLE, nên Approver.bizRole luôn null — backend
  // không còn mã nào để tra. Khoá dịch đã có sẵn đủ trong từ điển, chỉ thiếu cầu nối.
  "giám đốc": "DIRECTOR",
  "kế toán trưởng": "CHIEF_ACCOUNTANT",
  "kế toán": "ACCOUNTANT",
  "người đại diện": "REPRESENTATIVE_PERSON",
  "người đại diện pháp luật": "LEGAL_REPRESENTATIVE",
  "chủ tài khoản": "ACCOUNT_HOLDER",

  // ── guarantee (namnh): tên mẫu thư bảo lãnh (GRT_TEMPLATE.NAME) ──────────
  // BE trả tên mẫu tiếng Việt (#55917); dịch hiển thị ở dropdown "Guarantee letter template".
  "mẫu thư bảo lãnh dự thầu mbv bank - có điều kiện (tiếng việt)": "GRT_TPL_MBV_CONDITIONAL",
  "mẫu thư bảo lãnh dự thầu mbv bank - vô điều kiện (tiếng việt)": "GRT_TPL_MBV_UNCONDITIONAL",
  "theo mẫu đính kèm của tôi (theo hồ sơ mời thầu)": "GRT_TPL_MY_ATTACHED",
  "mẫu thư bảo lãnh dự thầu theo yêu cầu tập đoàn/tổng công ty (tiếng việt)": "GRT_TPL_GROUP_REQ",
  "mẫu thư bảo lãnh dự thầu theo quy định cơ quan nhà nước (tiếng việt)": "GRT_TPL_STATE_REQ",

  // ── OTP (namnh): resultDesc lỗi OTP do BE trả tiếng Việt, hiện thẳng ở toast
  // lỗi guarantee (#55924/#55975 reopen). Chuỗi TĨNH map trực tiếp; chuỗi ĐỘNG
  // "sai OTP N lần" xử lý bằng PATTERNS (giữ số N) ở dưới.
  "không tạo được giao dịch xác thực smart otp. vui lòng thử lại hoặc chọn sms otp.":
    "SMART_OTP_CREATE_FAILED",
};

/**
 * Mẫu câu BE ghép sẵn có tham số (số lần…). Khác UNITS ở chỗ dịch CẢ CÂU sang
 * một key i18n có {{p0}}, giữ nguyên phần số. Chỉ khớp câu đã liệt kê tường minh.
 */
const PATTERNS: { re: RegExp; key: string }[] = [
  // "Quý khách đã nhập sai mã OTP 1 lần. Vui lòng nhập lại." (#55924)
  {
    re: /^quý khách đã nhập sai mã otp\s+(\d+)\s+lần\.?\s*vui lòng nhập lại\.?$/,
    key: "TPL_WRONG_OTP_TIMES",
  },
];

/**
 * Chuẩn hoá để tra bảng: bỏ dấu hai chấm cuối, gộp khoảng trắng, về chữ thường.
 * KHÔNG bỏ dấu tiếng Việt — "Phi" và "Phí" là hai chuỗi khác nhau.
 */
const normalise = (s: string): string =>
  s.normalize("NFC").trim().replace(/:$/, "").trim().replace(/\s+/g, " ").toLowerCase();

/** Đơn vị thời gian backend ghép sẵn vào chuỗi ("3 Tháng - 3.85%/năm", "120 ngày"). */
const UNITS: { re: RegExp; key: string }[] = [
  { re: /(\d+)\s*ngày/gi, key: "DAYS_LOWER" },
  { re: /(\d+)\s*tuần/gi, key: "WEEKS_LOWER" },
  { re: /(\d+)\s*tháng/gi, key: "MONTHS_LOWER" },
  { re: /(\d+)\s*năm(?!\s*\))/gi, key: "YEARS_LOWER" },
];

/**
 * Dịch một nhãn/giá trị do backend trả về. Không nhận ra thì trả nguyên văn.
 *
 * Dùng cho chuỗi NGẮN có ngữ nghĩa nghiệp vụ. KHÔNG dùng cho dữ liệu tự do của
 * khách hàng (tên công ty, nội dung chuyển tiền) — những chuỗi đó không khớp bảng
 * nên vẫn an toàn, nhưng gọi vào đây là thừa.
 */
export const beText = (raw?: string | null): string => {
  const value = String(raw ?? "");
  if (!value.trim()) return value;

  const key = normalise(value);
  const hit = DICTIONARY[key];
  if (hit) {
    const trailingColon = value.trim().endsWith(":") ? ":" : "";
    return i18n.t(hit) + trailingColon;
  }

  // Mẫu câu có tham số (vd "sai OTP N lần"): dịch cả câu, giữ nguyên số N.
  // Dùng find thay vòng for...of: `no-restricted-syntax` của dự án cấm for...of
  // (vòng lặp này từng làm nhánh develop không build được).
  const hitPattern = PATTERNS.map((p) => ({ p, m: key.match(p.re) })).find(
    (x) => x.m !== null,
  );
  if (hitPattern?.m) return i18n.t(hitPattern.p.key, { p0: hitPattern.m[1] });

  // Chuỗi ghép "số + đơn vị" backend sinh: dịch riêng phần đơn vị, giữ nguyên số.
  // "3 Tháng - 3.85%/năm" -> "3 months - 3.85%/year"; số và dấu giữ nguyên.
  if (/\d/.test(value) && /ngày|tuần|tháng|năm/i.test(value)) {
    return UNITS.reduce(
      (acc, u) => acc.replace(u.re, (_m, n) => `${n} ${i18n.t(u.key)}`),
      value,
    ).replace(/\/\s*năm/gi, `/${i18n.t("YEARS_LOWER")}`);
  }

  return value;
};

export default beText;
